import { createFileRoute, Link } from "@tanstack/react-router";
import heroMethod from "@/assets/hero-method.jpg";
import { MarketingHeroImage } from "@/components/site/MarketingHeroImage";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { CONTENT_IMAGE_SIZES, HERO_IMAGE_SIZES, heroPreloadLink } from "@/lib/marketing-images";
import { heroMethodPic } from "@/lib/marketing-images.hero-method";
import { impactPic } from "@/lib/marketing-images.impact";
import { rangePic } from "@/lib/marketing-images.range";
import { getEglyMethodPillars } from "@/lib/egly-method-pillars";
import { localBusinessJsonLd, pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { ArrowRight, Phone, Star } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => {
    const head = pageHead({
      title: m.home_meta_title(),
      description: m.home_meta_description(),
      path: "/",
      image: heroMethod,
      jsonLd: localBusinessJsonLd(),
    });
    return {
      ...head,
      links: [...(head.links ?? []), heroPreloadLink(heroMethodPic)],
    };
  },
  component: HomePage,
});

function getTestimonials() {
  return [
    {
      q: m.home_testimonial_1_quote(),
      a: m.home_testimonial_1_author(),
      role: m.home_testimonial_1_role(),
    },
    {
      q: m.home_testimonial_2_quote(),
      a: m.home_testimonial_2_author(),
      role: m.home_testimonial_2_role(),
    },
    {
      q: m.home_testimonial_3_quote(),
      a: m.home_testimonial_3_author(),
      role: m.home_testimonial_3_role(),
    },
  ];
}

function HomePage() {
  const stats = [
    { v: "#1", l: m.home_stat_iowa_instructor() },
    { v: "7×", l: m.home_stat_pga_teacher() },
    { v: "30+", l: m.home_stat_years() },
    { v: m.home_stat_students_value(), l: m.home_stat_students() },
  ];
  const pillars = getEglyMethodPillars();
  const testimonials = getTestimonials();

  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink">
        <MarketingHeroImage
          picture={heroMethodPic}
          alt={m.home_hero_image_alt()}
          sizes={HERO_IMAGE_SIZES}
          priority
          className="absolute inset-0 h-full w-full object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
        <div className="container-page relative page-hero-pad min-h-[52svh] md:min-h-[58svh]">
          <div className="max-w-2xl">
            <span className="eyebrow">{m.home_eyebrow()}</span>
            <h1 className="mt-5 font-display text-hero leading-[0.92] md:text-8xl">
              <EmphasizedTitle
                lead={m.home_brand_lead()}
                accent={m.home_brand_accent()}
                accentBlock
              />
            </h1>
            <p className="mt-7 max-w-xl font-body text-base text-foreground/80 md:text-lg">
              {m.home_hero_body()}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/schedule" className="btn-ember btn-ember-hover text-base">
                {m.nav_book_lesson()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
              </Link>
              <Link
                to="/method"
                className="inline-flex items-center gap-2 font-display text-sm tracking-[0.18em] text-foreground/90 hover:text-ember"
              >
                {m.home_cta_method()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-white/5 bg-black">
        <div className="container-page grid grid-cols-2 gap-y-8 py-12 md:grid-cols-4">
          {stats.map((s) => (
            <div key={s.l} className="text-center">
              <div className="font-display text-4xl text-ember md:text-5xl">{s.v}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-page page-section-pad">
        <div className="grid items-end gap-8 md:grid-cols-12">
          <div className="md:col-span-5">
            <span className="eyebrow">{m.nav_method()}</span>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] md:text-6xl">
              <EmphasizedTitle
                lead={m.home_section_steps_lead()}
                accent={m.home_section_steps_accent()}
                stacked
              />
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 text-foreground/70">{m.home_method_blurb()}</p>
        </div>

        <div className="mt-14 grid gap-px bg-white/10 md:grid-cols-3">
          {pillars.map((p) => (
            <div key={p.n} className="group bg-ink p-9 transition-colors hover:bg-ink-soft">
              <div className="font-display text-7xl text-ember/80 group-hover:text-ember">
                {p.n}
              </div>
              <h3 className="mt-6 font-display text-2xl">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/70">{p.summary}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Link to="/method" className="btn-ember btn-ember-hover">
            {m.home_cta_start_method()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <section className="bg-black">
        <div className="container-page grid items-center gap-14 page-section-pad md:grid-cols-2">
          <div className="relative">
            <MarketingHeroImage
              picture={impactPic}
              alt={m.home_impact_image_alt()}
              sizes={CONTENT_IMAGE_SIZES}
              className="aspect-[4/3] w-full object-cover"
            />
            <div
              className="absolute -bottom-4 -right-4 hidden bg-ember-fill px-5 py-3 font-display text-sm tracking-[0.18em] text-foreground md:block"
              style={{ clipPath: "polygon(0 0, 100% 0, calc(100% - 12px) 100%, 0 100%)" }}
            >
              {m.home_impact_matters()}
            </div>
          </div>
          <div>
            <span className="eyebrow">{m.home_eyebrow_why()}</span>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] md:text-6xl">
              <EmphasizedTitle
                lead={m.home_section_programs_lead()}
                accent={m.home_section_programs_accent()}
                stacked
              />
            </h2>
            <p className="mt-5 text-foreground/70">{m.home_why_body()}</p>
            <ul className="mt-7 space-y-3 text-sm">
              {[
                m.home_feature_full_swing(),
                m.home_feature_video(),
                m.home_feature_junior(),
                m.home_feature_year_round(),
              ].map((i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 border-s-2 border-ember ps-4 text-foreground/85"
                >
                  <span>{i}</span>
                </li>
              ))}
            </ul>
            <Link to="/lessons" className="btn-ember btn-ember-hover mt-9">
              {m.home_cta_lesson_options()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-ink">
        <div className="container-page page-section-pad">
          <div className="mx-auto max-w-2xl text-center">
            <span className="eyebrow">{m.home_eyebrow_testimonials()}</span>
            <h2 className="mt-3 font-display text-5xl md:text-6xl">
              {m.home_section_testimonials()}
            </h2>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure key={t.a} className="border border-white/10 bg-black/40 p-8">
                <div className="flex gap-1 text-ember">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-ember" />
                  ))}
                </div>
                <blockquote className="mt-5 font-heading text-lg leading-relaxed text-foreground/90">
                  "{t.q}"
                </blockquote>
                <figcaption className="mt-6 font-display text-xs tracking-[0.18em] text-foreground/60">
                  <span className="text-foreground">{t.a}</span> · {t.role}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden bg-black">
        <MarketingHeroImage
          picture={rangePic}
          alt=""
          sizes={HERO_IMAGE_SIZES}
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="container-page relative page-section-pad">
          <div className="max-w-2xl">
            <span className="eyebrow">{m.home_eyebrow_started()}</span>
            <h2 className="mt-3 font-display text-5xl leading-[0.95] md:text-7xl">
              <EmphasizedTitle
                lead={m.home_section_ready_lead()}
                accent={m.home_section_ready_accent()}
                stacked
              />
            </h2>
            <p className="mt-5 max-w-lg text-foreground/75">{m.home_ready_body()}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/schedule" className="btn-ember btn-ember-hover">
                {m.nav_book_lesson()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
              </Link>
              <a
                href="tel:5152891703"
                className="inline-flex items-center gap-2 border border-white/30 px-5 py-3 font-display tracking-[0.15em] text-foreground hover:bg-white/5"
              >
                <Phone className="h-4 w-4" /> 515-289-1703
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
